import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Flame, Award, Trophy, Gamepad2, MapPin, Globe, Calendar, UserPlus, UserMinus, Users, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { API_URL } from '../config/api';
import { toast } from 'sonner';

const PublicProfile = ({ user: currentUser }) => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const isOwnProfile = currentUser && profile && currentUser.id === profile.id;

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await axios.get(`${API_URL}/api/users/username/${username}`);
      setProfile(res.data);

      // Fetch stats and badges in parallel
      const [statsRes, badgesRes] = await Promise.all([
        axios.get(`${API_URL}/api/dashboard-stats/${res.data.id}`).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/badges/${res.data.id}`).catch(() => ({ data: {} })),
      ]);
      setStats(statsRes.data);
      setBadges(badgesRes.data);

      // Check follow status
      if (currentUser && currentUser.id !== res.data.id) {
        const followRes = await axios.get(`${API_URL}/api/users/${currentUser.id}/is-following/${res.data.id}`);
        setIsFollowing(followRes.data.is_following);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
      }
    } finally {
      setLoading(false);
    }
  }, [username, currentUser]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleFollow = async () => {
    if (!currentUser) {
      toast.error('Please log in to follow users');
      return;
    }
    if (!profile || isOwnProfile) return;
    setFollowLoading(true);
    try {
      await axios.post(`${API_URL}/api/users/${profile.id}/follow?follower_id=${currentUser.id}`);
      setIsFollowing(true);
      setProfile(prev => ({ ...prev, followers_count: (prev.followers_count || 0) + 1 }));
      toast.success(`Following @${profile.username}`);
    } catch (err) {
      if (err.response?.status === 400) {
        setIsFollowing(true);
      } else {
        toast.error('Failed to follow');
      }
    } finally {
      setFollowLoading(false);
    }
  };

  const handleUnfollow = async () => {
    if (!currentUser || !profile) return;
    setFollowLoading(true);
    try {
      await axios.delete(`${API_URL}/api/users/${profile.id}/follow?follower_id=${currentUser.id}`);
      setIsFollowing(false);
      setProfile(prev => ({ ...prev, followers_count: Math.max((prev.followers_count || 1) - 1, 0) }));
      toast.success(`Unfollowed @${profile.username}`);
    } catch {
      toast.error('Failed to unfollow');
    } finally {
      setFollowLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white gap-4">
        <Users className="h-12 w-12 text-gray-600" />
        <p className="text-lg text-gray-400">User not found</p>
        <Button onClick={() => navigate(-1)} variant="outline" className="border-zinc-700 text-gray-300" data-testid="profile-go-back">Go Back</Button>
      </div>
    );
  }

  if (!profile) return null;

  const totalBadgesEarned = badges?.total_earned || 0;
  const gameBadges = (badges?.game_badges || []).filter(b => b.earned);
  const movieBadges = (badges?.movie_badges || []).filter(b => b.earned);
  const genreBadges = (badges?.genre_badges || []).filter(b => b.earned);
  const earnedBadges = [...gameBadges, ...movieBadges, ...genreBadges];

  const BADGE_EMOJIS = {
    gamepad: '\ud83c\udfae', flame: '\ud83d\udd25', target: '\ud83c\udfaf', trophy: '\ud83c\udfc6',
    swords: '\u2694\ufe0f', film: '\ud83c\udfac', crown: '\ud83d\udc51', pen: '\u270f\ufe0f',
    users: '\ud83d\udc65', book: '\ud83d\udcd6', star: '\u2b50', tv: '\ud83d\udcfa',
    skull: '\ud83d\udc80', rocket: '\ud83d\ude80', laugh: '\ud83d\ude02', theater: '\ud83c\udfad',
    zap: '\u26a1', globe: '\ud83c\udf0d', medal: '\ud83c\udf96\ufe0f',
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="public-profile-page">
      {/* Cover */}
      <div className="relative">
        <div className="h-36 sm:h-48 bg-gradient-to-br from-zinc-900 via-zinc-800 to-amber-950" />
        <button
          onClick={() => navigate(-1)}
          className="absolute top-4 left-4 p-2 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur z-10"
          data-testid="public-profile-back-btn"
        >
          <ArrowLeft className="h-5 w-5 text-white" />
        </button>

        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="relative -mt-14 sm:-mt-16 flex items-end gap-4">
            {/* Avatar */}
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full border-4 border-black bg-zinc-800 flex items-center justify-center text-2xl sm:text-3xl font-bold text-white overflow-hidden flex-shrink-0" data-testid="public-profile-avatar">
              {profile.profile_picture ? (
                <img src={profile.profile_picture} alt="" className="w-full h-full object-cover" />
              ) : (
                `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`.toUpperCase()
              )}
            </div>
            <div className="pb-1 flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold truncate" data-testid="public-profile-name">
                {profile.display_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim()}
              </h1>
              {profile.username && <p className="text-gray-400 text-sm" data-testid="public-profile-username">@{profile.username}</p>}
            </div>
            {/* Follow/Unfollow Button */}
            {currentUser && !isOwnProfile && (
              <div className="flex-shrink-0 pb-1">
                {isFollowing ? (
                  <Button
                    onClick={handleUnfollow}
                    disabled={followLoading}
                    variant="outline"
                    className="border-zinc-600 text-gray-300 hover:border-red-700 hover:text-red-400 hover:bg-red-950/30 h-9 px-4 text-sm gap-1.5"
                    data-testid="unfollow-btn"
                  >
                    {followLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserMinus className="h-4 w-4" />}
                    Following
                  </Button>
                ) : (
                  <Button
                    onClick={handleFollow}
                    disabled={followLoading}
                    className="bg-amber-600 hover:bg-amber-700 text-white h-9 px-4 text-sm gap-1.5"
                    data-testid="follow-btn"
                  >
                    {followLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                    Follow
                  </Button>
                )}
              </div>
            )}
            {isOwnProfile && (
              <div className="flex-shrink-0 pb-1">
                <Button
                  onClick={() => navigate('/profile')}
                  variant="outline"
                  className="border-zinc-600 text-gray-300 h-9 px-4 text-sm"
                  data-testid="edit-profile-btn"
                >
                  Edit Profile
                </Button>
              </div>
            )}
          </div>

          {/* Bio & Meta */}
          {profile.bio && <p className="text-gray-300 text-sm mt-3 max-w-xl">{profile.bio}</p>}
          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 flex-wrap">
            {profile.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{profile.location}</span>}
            {profile.website && (
              <a href={profile.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-blue-400 hover:underline">
                <Globe className="h-3 w-3" />{profile.website.replace(/https?:\/\//, '')}
              </a>
            )}
            {profile.created_at && (
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Joined {new Date(profile.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </span>
            )}
          </div>

          {/* Follower / Following counts */}
          <div className="flex items-center gap-4 mt-3" data-testid="public-profile-follow-counts">
            <span className="text-sm">
              <span className="text-white font-semibold">{profile.following_count || 0}</span>
              <span className="text-gray-500 ml-1">Following</span>
            </span>
            <span className="text-sm">
              <span className="text-white font-semibold">{profile.followers_count || 0}</span>
              <span className="text-gray-500 ml-1">Followers</span>
            </span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="public-profile-stats">
          <div className="bg-gradient-to-br from-amber-900/30 to-zinc-800 rounded-xl p-3 sm:p-4 border border-amber-800/30 text-center">
            <Flame className="h-4 w-4 sm:h-5 sm:w-5 text-amber-400 mx-auto mb-1" />
            <p className="text-xl sm:text-2xl font-black text-amber-400">{stats?.casita_points ?? 0}</p>
            <p className="text-[10px] sm:text-[11px] text-gray-400 font-medium">CasitaPoints</p>
          </div>
          <div className="bg-zinc-800 rounded-xl p-3 sm:p-4 border border-zinc-700 text-center">
            <Trophy className="h-4 w-4 sm:h-5 sm:w-5 text-amber-500 mx-auto mb-1" />
            <p className="text-xl sm:text-2xl font-black text-white">#{stats?.rank ?? '—'}</p>
            <p className="text-[10px] sm:text-[11px] text-gray-400 font-medium">Global Rank</p>
          </div>
          <div className="bg-zinc-800 rounded-xl p-3 sm:p-4 border border-zinc-700 text-center">
            <Award className="h-4 w-4 sm:h-5 sm:w-5 text-purple-400 mx-auto mb-1" />
            <p className="text-xl sm:text-2xl font-black text-purple-400">{totalBadgesEarned}</p>
            <p className="text-[10px] sm:text-[11px] text-gray-400 font-medium">Badges</p>
          </div>
          <div className="bg-zinc-800 rounded-xl p-3 sm:p-4 border border-zinc-700 text-center">
            <Gamepad2 className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-400 mx-auto mb-1" />
            <p className="text-xl sm:text-2xl font-black text-white">{stats?.total_played ?? 0}</p>
            <p className="text-[10px] sm:text-[11px] text-gray-400 font-medium">Games</p>
          </div>
        </div>
      </div>

      {/* Earned Badges */}
      {earnedBadges.length > 0 && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-6">
          <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
            <Award className="h-4 w-4 text-purple-400" />Badges ({earnedBadges.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {earnedBadges.map(b => {
              const emoji = BADGE_EMOJIS[b.icon] || '\ud83c\udfc5';
              return (
                <div
                  key={b.id}
                  className="flex items-center gap-1.5 bg-zinc-800 border border-amber-800/30 rounded-lg px-2.5 py-1.5"
                  title={b.description}
                  data-testid={`public-badge-${b.id}`}
                >
                  <span className="text-base">{emoji}</span>
                  <span className="text-[11px] text-white font-medium">{b.name}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Clubs */}
      {profile.clubs && profile.clubs.length > 0 && (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-6">
          <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
            <Users className="h-4 w-4 text-purple-400" />Clubs ({profile.clubs.length})
          </h3>
          <div className="flex flex-wrap gap-2">
            {profile.clubs.map(c => (
              <div key={c.id} className="flex items-center gap-1.5 bg-zinc-800 border border-purple-800/30 rounded-lg px-2.5 py-1.5" data-testid={`public-club-${c.id}`}>
                <Users className="h-3.5 w-3.5 text-purple-400" />
                <span className="text-[11px] text-white font-medium">{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Spacer for mobile bottom nav */}
      <div className="h-20" />
    </div>
  );
};

export default PublicProfile;
